import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import type { TFunction } from "i18next";
import { RotateCcw } from "lucide-react";
import type { AdminMediaResponse } from "@ckgo2/shared/types";
import { mediaFolderLabel } from "@/lib/mediaFormat";
import { mediaDetailHref } from "@/hooks/useMediaLibrary";

/**
 * Dlaždice stránky Média (vize podnety-tymu-2026-10, F7.5). Čistá komponenta bez hooků:
 * `t` nese v props, aby šla vykreslit v testu bez kontextu.
 *
 * Kořen je `<div>`, ne `<button>`: zaškrtávátko uvnitř tlačítka je neplatné HTML. Tlačítko
 * přes celou dlaždici a zaškrtávátko jsou sourozenci, klik na zaškrtávátko tedy do tlačítka
 * nebublá. Dlaždice není `<a>`: Cmd-klik na odkaz by otevřel nový panel místo výběru.
 */

export type TileClickIntent = "open" | "toggle" | "extend";

/** Shift vyhrává (i s Cmd), pak Cmd/Ctrl; zaškrtávátko bez Shiftu vždy toggle. */
export function tileClickIntent(
  e: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
  target: "tile" | "checkbox",
): TileClickIntent {
  if (e.shiftKey) return "extend";
  if (target === "checkbox" || e.metaKey || e.ctrlKey) return "toggle";
  return "open";
}

export interface MediaTileProps {
  item: AdminMediaResponse;
  selected: boolean;
  trashMode: boolean;
  restoring: boolean;
  t: TFunction;
  onIntent: (id: string, intent: TileClickIntent) => void;
  onRestore: (id: string) => void;
}

/** Shift-klik by jinak označil text stránky mezi kotvou a dlaždicí. */
const preventShiftSelection = (e: { shiftKey: boolean; preventDefault: () => void }) => {
  if (e.shiftKey) e.preventDefault();
};

export default function MediaTile({
  item,
  selected,
  trashMode,
  restoring,
  t,
  onIntent,
  onRestore,
}: MediaTileProps): ReactElement {
  const name = item.alt || item.originalName || item.id;
  const usageLabel = t("media.page.usageCount", { count: item.usageCount });
  return (
    <div
      data-media-tile=""
      data-media-id={item.id}
      data-starred={item.isStarred ? "true" : "false"}
      data-usage-count={item.usageCount}
      className={`group relative overflow-hidden rounded-lg border-2 transition-all ${
        selected
          ? "border-primary-500 ring-primary-500/30 ring-2"
          : "border-transparent hover:border-gray-200 dark:hover:border-gray-700"
      }`}
    >
      <button
        type="button"
        aria-label={name}
        title={name}
        onMouseDown={preventShiftSelection}
        onClick={(e) => onIntent(item.id, tileClickIntent(e, "tile"))}
        className="block w-full text-left"
      >
        <div className="relative aspect-square overflow-hidden bg-gray-100 dark:bg-gray-800">
          <img
            src={item.thumbUrl}
            alt={item.alt}
            loading="lazy"
            className="h-full w-full object-cover"
          />
          <span
            aria-label={usageLabel}
            title={usageLabel}
            className={`pointer-events-none absolute bottom-1.5 right-1.5 min-w-[1.5rem] rounded-full px-1.5 py-0.5 text-center text-[11px] font-semibold shadow ${
              item.usageCount > 0 ? "bg-gray-900/75 text-white" : "bg-white/85 text-gray-500"
            }`}
          >
            {item.usageCount}
          </span>
        </div>
        <div className="p-1.5">
          <p className="truncate text-xs font-medium text-gray-900 dark:text-white">{name}</p>
          <p className="truncate text-[11px] text-gray-500">{mediaFolderLabel(item.folder)}</p>
        </div>
      </button>

      <input
        type="checkbox"
        data-media-select=""
        checked={selected}
        readOnly
        aria-label={t("media.page.select", { name })}
        onMouseDown={preventShiftSelection}
        onClick={(e) => onIntent(item.id, tileClickIntent(e, "checkbox"))}
        className={`text-primary-600 focus:ring-primary-500 absolute left-1.5 top-1.5 h-4 w-4 cursor-pointer rounded border-gray-300 shadow transition-opacity ${
          selected ? "opacity-100" : "opacity-70 group-hover:opacity-100"
        }`}
      />

      {item.isStarred && (
        <span
          aria-label={t("media.page.starred")}
          title={t("media.page.starred")}
          className="pointer-events-none absolute right-1.5 top-1.5 text-lg leading-none text-amber-400 drop-shadow"
        >
          ★
        </span>
      )}

      {trashMode && (
        <div className="px-1.5 pb-1.5">
          {item.liveTwinId ? (
            <Link
              to={mediaDetailHref(item.liveTwinId)}
              data-media-live-twin=""
              title={t("media.page.liveTwinTitle")}
              className="text-primary-600 hover:text-primary-700 text-xs font-medium underline"
            >
              {t("media.page.liveTwin")}
            </Link>
          ) : (
            <button
              type="button"
              data-media-restore=""
              disabled={restoring}
              onClick={() => onRestore(item.id)}
              className="btn-ghost w-full justify-center text-xs"
            >
              <RotateCcw className="h-3.5 w-3.5" /> {t("media.actions.restore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
